<ng-container *ngIf="isLoading; else resultsContent">
  <app-loader />
</ng-container>

<ng-template #resultsContent>
  <!-- Error State -->
  <div *ngIf="hasError" class="container py-5">
    <div class="alert alert-danger text-center" role="alert">
      <i class="fas fa-exclamation-triangle me-2"></i>
      <p>{{ errorMessage }}</p>
      <button class="btn btn-primary mt-3" (click)="goBackToTests()">
        <i class="fas fa-arrow-left me-2"></i>
        {{ 'BUTTONS.BACK_TO_TESTS' | translate }}
      </button>
    </div>
  </div>

  <!-- Results Content -->
  <app-card *ngIf="!hasError && testResult" [customClasses]="'d-flex justify-content-center p-4 bg-dashboard-card'">
    <app-card [customClasses]="'p-2 bg-quiz'">
      <!-- Header -->
      <!-- La durée réelle de la copie : sans elle, l'en-tête affichait un 50:00 figé (SKILL2ERA-90). -->
      <app-quiz-header [iconStart]="'fa fa-trophy'" [iconEnd]="'fa-regular fa-chart-bar'"
        [totalSeconds]="testResult.timeSpent || null" [timerAlerts]="false"
        [customClasses]="'p-3 bg-light border-bottom'">
        <div class="d-flex flex-column">
          <h2 class="fs-20 fg-600 text-dark mb-1">
            <i class="fas fa-check-circle me-2" [ngClass]="testResult.passed ? 'text-success' : 'text-danger'"></i>
            {{ 'TESTS.TEST_RESULTS' | translate }}
          </h2>
          <span class="fs-14 text-muted">{{ testResult.test.title }}</span>
        </div>
      </app-quiz-header>

      <!-- Success/Failure Alert -->
      <div class="alert mx-3 mt-3" [ngClass]="testResult.passed ? 'alert-success' : 'alert-danger'" role="alert">
        <div class="d-flex align-items-center">
          <i class="me-3" [ngClass]="testResult.passed ? 'fas fa-trophy fs-2' : 'fas fa-info-circle fs-2'"></i>
          <div>
            <h4 class="alert-heading mb-1">
              {{ testResult.passed ? ('TESTS.CONGRATULATIONS' | translate) : ('TESTS.NEEDS_IMPROVEMENT' | translate) }}
            </h4>
            <!-- Sur un échec, le message dit ce que l'élève peut réellement faire :
                 recommencer s'il lui reste une tentative, sinon passer à la
                 suite. Il promettait auparavant une reprise à tout le monde. -->
            <p class="mb-0">
              {{ testResult.passed ? ('TESTS.PASSED_MESSAGE' | translate) : (messageEchec | translate: { count: tentativesRestantes }) }}
            </p>
          </div>
        </div>
      </div>

      <!-- Score Summary Section -->
      <div class="row g-3 p-3">
        <!-- Score Card -->
        <div class="col-md-6">
          <app-card [customClasses]="'p-4 h-100 border shadow-sm'">
            <div class="text-center">
              <h3 class="fs-18 fg-600 text-muted mb-3">{{ 'TESTS.YOUR_SCORE' | translate }}</h3>
              <!-- La note sur le barème passe devant le pourcentage : c'est
                   elle qui se reporte sur un bulletin. Le pourcentage reste
                   affiché, il n'est pas remplacé. -->
              <div class="score-display mb-3">
                <h1 class="display-3 fg-700 mb-0" [ngClass]="getScoreColor()">
                  {{ scaledScore | number:'1.0-2' }} / {{ gradingScale }}
                </h1>
                <p class="fs-18 text-muted mb-0">
                  {{ testResult.percentage | number:'1.0-1' }}%
                </p>
              </div>
              <div class="d-flex justify-content-center gap-4 text-muted">
                <div>
                  <i class="fas fa-star text-warning me-1"></i>
                  <strong>{{ testResult.score }}</strong> / {{ totalPoints }}
                  {{ 'TESTS.POINTS' | translate }}
                </div>
                <!-- Deux grandeurs distinctes, longtemps confondues sous une
                     seule horloge sans unité : le temps réellement passé, et
                     la durée que le test accorde. -->
                <div>
                  <i class="fas fa-clock me-1"></i>
                  {{ 'TESTS.TIME_SPENT' | translate }}: <strong>{{ timeSpentLabel }}</strong>
                </div>
                <div *ngIf="timeLimitLabel">
                  <i class="fas fa-hourglass-half me-1"></i>
                  {{ 'TESTS.TIME_ALLOWED' | translate }}: <strong>{{ timeLimitLabel }}</strong>
                </div>
              </div>

              <!-- Passing Score Info -->
              <div class="mt-3 pt-3 border-top" *ngIf="testResult.test.passingPercentage">
                <small class="text-muted">
                  {{ 'TESTS.PASSING_SCORE' | translate }}: {{ testResult.test.passingPercentage }}%
                </small>
              </div>
            </div>
          </app-card>
        </div>

        <!-- Chart Card -->
        <div class="col-md-6">
          <app-card [customClasses]="'p-4 h-100 border shadow-sm'">
            <h3 class="fs-18 fg-600 text-center text-muted mb-3">{{ 'TESTS.ANSWER_BREAKDOWN' | translate }}</h3>
            <app-pie [chartData]="chartData"></app-pie>
          </app-card>
        </div>
      </div>

      <!-- Statistics Section -->
      <div class="p-3">
        <app-card [customClasses]="'p-4 border shadow-sm'">
          <h3 class="fs-18 fg-600 text-muted mb-3">{{ 'TESTS.STATISTICS' | translate }}</h3>
          <div class="row g-3">
            <!-- Total Questions -->
            <div class="col-6 col-md-3">
              <div class="stat-box text-center p-3 rounded bg-light">
                <i class="fas fa-list-ol text-primary fs-3 mb-2"></i>
                <div class="fs-24 fg-700">{{ testResult.totalQuestions }}</div>
                <small class="text-muted">{{ 'TESTS.TOTAL_QUESTIONS' | translate }}</small>
              </div>
            </div>
            

            <!-- Correct Answers -->
            <div class="col-6 col-md-3">
              <div class="stat-box text-center p-3 rounded bg-success-subtle">
                <i class="fas fa-check-circle text-success fs-3 mb-2"></i>
                <div class="fs-24 fg-700 text-success">{{ testResult.correctAnswers }}</div>
                <small class="text-muted">{{ 'TESTS.CORRECT' | translate }}</small>
              </div>
            </div>

            <!-- Wrong Answers -->
            <div class="col-6 col-md-3">
              <div class="stat-box text-center p-3 rounded bg-danger-subtle">
                <i class="fas fa-times-circle text-danger fs-3 mb-2"></i>
                <div class="fs-24 fg-700 text-danger">{{ testResult.wrongAnswers }}</div>
                <small class="text-muted">{{ 'TESTS.WRONG' | translate }}</small>
              </div>
            </div>

            <!-- Timed Out (if any) -->
            <div class="col-6 col-md-3" *ngIf="testResult.timedOutQuestions > 0">
              <div class="stat-box text-center p-3 rounded bg-warning-subtle">
                <i class="fas fa-clock text-warning fs-3 mb-2"></i>
                <div class="fs-24 fg-700 text-warning">{{ testResult.timedOutQuestions }}</div>
                <small class="text-muted">{{ 'TESTS.TIMED_OUT' | translate }}</small>
              </div>
            </div>

            <!-- Unanswered (if any) -->
            <div class="col-6 col-md-3" *ngIf="testResult.unansweredQuestions > 0">
              <div class="stat-box text-center p-3 rounded bg-secondary-subtle">
                <i class="fas fa-circle text-secondary fs-3 mb-2"></i>
                <div class="fs-24 fg-700 text-secondary">{{ testResult.unansweredQuestions }}</div>
                <small class="text-muted">{{ 'TESTS.UNANSWERED' | translate }}</small>
              </div>
            </div>
          
          
                <!-- 🔥 Tab Switch Violations -->
      <div class="col-6 col-md-3" *ngIf="behavior">
        <div class="stat-box text-center p-3 rounded bg-warning-subtle">
          <i class="fas fa-exchange-alt text-warning fs-3 mb-2"></i>
          <div class="fs-24 fg-700 text-warning">
            {{ behavior.tabSwitchCount }}
          </div>
        <small class="text-muted">
          {{ 'TESTS.TAB_SWITCHES' | translate }}
        </small>
        </div>
      </div>

      <!-- 🔥 Focus Loss Count -->
      <div class="col-6 col-md-3" *ngIf="behavior">
        <div class="stat-box text-center p-3 rounded bg-warning-subtle">
          <i class="fas fa-eye-slash text-warning fs-3 mb-2"></i>
          <div class="fs-24 fg-700 text-warning">
            {{ behavior.focusLossCount }}
          </div>
          <small class="text-muted">
            {{ 'TESTS.FOCUS_LOSSES' | translate }}
          </small>
        </div>
      </div>

      <!-- 🔥 Fullscreen Exit Count -->
      <div class="col-6 col-md-3" *ngIf="behavior">
        <div class="stat-box text-center p-3 rounded bg-danger-subtle">
          <i class="fas fa-expand-arrows-alt text-danger fs-3 mb-2"></i>
          <div class="fs-24 fg-700 text-danger">
            {{ behavior.fullscreenExitCount }}
          </div>
          <small class="text-muted">
            {{ 'TESTS.FULLSCREEN_EXITS' | translate }}
          </small>
        </div>
      </div>
          
          
          
          </div>
        </app-card>
      </div>

      <!-- Detailed Results Toggle -->
      <div class="p-3">
        <app-card [customClasses]="'p-3 border shadow-sm bg-light'">
          <div class="d-flex justify-content-between align-items-center">
            <h3 class="fs-18 fg-600 text-muted mb-0">{{ 'TESTS.DETAILED_RESULTS' | translate }}</h3>
            <app-button (click)="toggleDetailedResults()"
              [label]="showDetailedResults ? ('BUTTONS.HIDE' | translate) : ('BUTTONS.SHOW' | translate)"
              [icon]="showDetailedResults ? 'fa fa-chevron-up' : 'fa fa-chevron-down'" [iconPosition]="'end'"
              [customClasses]="'btn-sm btn-next p-2'" [type]="'button'">
            </app-button>
          </div>
        </app-card>
      </div>

      <!-- Question-by-Question Results -->
      <div class="p-3" *ngIf="showDetailedResults">
        <app-card [customClasses]="'p-3 border shadow-sm'">
          <div class="question-results">
            <div *ngFor="let result of testResult.questionResults; let i = index" class="question-result-item mb-3">
              <!-- Question Header -->
              <div class="question-header p-3 rounded cursor-pointer"
                [ngClass]="getQuestionBadgeClass(result) + '-subtle'" (click)="toggleQuestionDetails(i)">
                <div class="d-flex justify-content-between align-items-center">
                  <div class="d-flex align-items-center gap-3">
                    <i [class]="getQuestionIcon(result)" class="fs-4"></i>
                  <div>
                      <strong class="fs-16">
                        {{ 'TESTS.QUESTION' | translate }} {{ i + 1 }}
                      </strong>

                      <div class="text-muted small mt-1">
                        <i class="fas fa-clock me-1"></i>
                        {{ 'TESTS.TIME_SPENT' | translate }}: {{ result.timeSpent | duration }}
                      </div>

                      <p class="mb-0 text-muted small">
                        <app-math-text [text]="result.question.question.title"></app-math-text>
                      </p>
                    </div>
                  </div>
                  <div class="d-flex align-items-center gap-3">
                    <span class="badge" [ngClass]="getQuestionBadgeClass(result)">
                      {{ result.earnedPoints }} / {{ result.points }} {{ 'TESTS.POINTS' | translate }}
                    </span>
                    <i class="fas" [class.fa-chevron-down]="expandedQuestionIndex !== i"
                      [class.fa-chevron-up]="expandedQuestionIndex === i"></i>
                  </div>
                </div>
              </div>

              <!-- Question Details (Expanded) -->
              <div class="question-details p-3 border-top" *ngIf="expandedQuestionIndex === i">
                <!-- Question Content -->
                <div class="question-content mb-3" *ngIf="result.question.question.description">
                  <p class="text-muted"><app-math-text [text]="result.question.question.description"></app-math-text></p>
                </div>

                <!-- Timed Out Message -->
                <div class="alert alert-warning mb-3" *ngIf="result.isTimedOut">
                  <i class="fas fa-clock me-2"></i>
                  <strong>{{ 'TESTS.QUESTION_TIMED_OUT' | translate }}</strong>
                  <p class="mb-0 small">{{ 'TESTS.NO_ANSWER_SUBMITTED' | translate }}</p>
                </div>

                <!-- Answers -->
                <div class="answers-list" *ngIf="!result.isTimedOut">
                  <div *ngFor="let answer of result.question.question.answerBanks; let j = index"
                    class="answer-item p-3 mb-2 rounded border"
                    [ngClass]="getAnswerClass(answer.id, result)">
                    <div class="d-flex align-items-start gap-3">
                      <!-- Answer Label -->
                      <span class="badge bg-secondary">{{ getAnswerLabel(j) }}</span>

                      <!-- Answer Content -->
                      <div class="flex-grow-1">
                        <p class="mb-0"><app-math-text [text]="answer.content"></app-math-text></p>
                      </div>

                      <!-- Answer Indicators -->
                      <div class="answer-indicators d-flex gap-2">
                        <!-- User's Answer -->
                        <span *ngIf="isAnswerSelected(answer.id, result)" class="badge bg-info">
                          <i class="fas fa-user me-1"></i>
                          {{ 'TESTS.YOUR_ANSWER' | translate }}
                        </span>

                        <!-- Correct Answer -->
                        <span *ngIf="isAnswerCorrect(answer.id, result)" class="badge bg-success">
                          <i class="fas fa-check me-1"></i>
                          {{ 'TESTS.CORRECT' | translate }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Explanation (if available) -->
                <div class="explanation mt-3 p-3 bg-light rounded" *ngIf="result.question.question.explanation">
                  <h6 class="text-muted mb-2">
                    <i class="fas fa-lightbulb me-2"></i>
                    {{ 'TESTS.EXPLANATION' | translate }}
                  </h6>
                  <p class="mb-0 small"><app-math-text [text]="result.question.question.explanation"></app-math-text></p>
                </div>
              </div>
            </div>
          </div>
        </app-card>
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons d-flex justify-content-between p-3 gap-2 border-top">
        <app-button (click)="goBackToTests()" [label]="'BUTTONS.BACK_TO_TESTS' | translate"
          [icon]="isLTR ? 'fa fa-arrow-left' : 'fa fa-arrow-right'" [iconPosition]="'start'"
          [customClasses]="'btn-previous p-2'" [type]="'button'">
        </app-button>

        <!-- Rapport de diagnostic : le document livré, avec paliers,
             compétences et plan de remédiation. Réservé aux tests
             diagnostiques, seuls à évaluer des compétences.
             Plus d'attente ni de génération : le rapport est un écran, on y
             navigue. -->
        <app-button *ngIf="canOpenReport" (click)="openDiagnosticReport()"
          [label]="'TESTS.OPEN_REPORT' | translate"
          [icon]="'fa fa-file-lines'"
          [iconPosition]="'start'"
          [customClasses]="'btn-next p-2'" [type]="'button'">
        </app-button>

        <!-- Repasser n'est proposé que si le serveur l'accepterait : fenêtre
             de passation ouverte et tentatives restantes. Sans cette garde, le
             bouton menait droit à un refus d'ouverture de session. -->
        <app-button *ngIf="canRetake" (click)="retakeTest()" [label]="'TESTS.RETAKE_TEST' | translate"
          [icon]="'fa fa-redo'"
          [iconPosition]="'end'" [customClasses]="'btn-previous p-2'" [type]="'button'">
        </app-button>
      </div>
    </app-card>
  </app-card>
</ng-template>